import { useState } from "react";
import { toast } from "sonner";
import { Zap, Maximize2, MoveHorizontal, Bell, BellOff, Repeat, Type, Palette, Music2, Play, Bookmark } from "lucide-react";
import { createSchedule } from "@/lib/api";
import { localInputToIso } from "@/lib/time";
import { primeAudio, startTone, stopAllTones, TONE_LABELS } from "@/lib/beeper";
import { formatLeadTime } from "@/lib/format";
import { useI18n } from "@/lib/i18n";
import SaveTemplateDialog from "@/components/SaveTemplateDialog";

const defaultDateTime = () => {
  const d = new Date(Date.now() + 60 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PRESETS = [
  { label: "5m", value: 5 },
  { label: "10m", value: 10 },
  { label: "30m", value: 30 },
  { label: "1h", value: 60 },
  { label: "1d", value: 1440 },
];

export const SchedulerForm = ({ onCreated, onTemplateSaved }) => {
  const { t } = useI18n();
  const [message, setMessage] = useState("");
  const [when, setWhen] = useState(defaultDateTime());
  const [alarmTone, setAlarmTone] = useState("beep");
  const [previewing, setPreviewing] = useState(null);
  const [displayMode, setDisplayMode] = useState("fit");
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderMinutes, setReminderMinutes] = useState(10);
  const [customReminder, setCustomReminder] = useState("");
  const [repeat, setRepeat] = useState("none");
  const [textSize, setTextSize] = useState("md");
  const [bgEnabled, setBgEnabled] = useState(false);
  const [bgColors, setBgColors] = useState(["#FF3311"]);
  const [submitting, setSubmitting] = useState(false);
  const [showSaveTpl, setShowSaveTpl] = useState(false);

  const openSaveTemplate = () => {
    if (!message.trim()) {
      toast.error(t("tpl.needMessage"));
      return;
    }
    setShowSaveTpl(true);
  };

  const templateDraft = () => {
    const rm = reminderEnabled
      ? customReminder
        ? parseInt(customReminder, 10)
        : reminderMinutes
      : null;
    return {
      message: message.trim(),
      display_mode: displayMode,
      repeat,
      reminder_minutes: rm && rm > 0 ? rm : null,
      text_size: textSize,
      bg_colors: bgEnabled ? bgColors : null,
      alarm_tone: alarmTone,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Ignore synthetic submits that bubbled from a nested/portal'd form
    if (e.target !== e.currentTarget) return;
    primeAudio();
    if (!message.trim()) {
      toast.error("MESSAGE REQUIRED");
      return;
    }
    const iso = localInputToIso(when);
    if (!iso) {
      toast.error("INVALID DATE / TIME");
      return;
    }

    let rm = null;
    if (reminderEnabled) {
      rm = customReminder ? parseInt(customReminder, 10) : reminderMinutes;
      if (!rm || rm <= 0 || isNaN(rm)) {
        toast.error("INVALID REMINDER TIME");
        return;
      }
      // Ensure reminder is in the future
      const reminderMs = new Date(iso).getTime() - rm * 60 * 1000;
      if (reminderMs <= Date.now()) {
        toast.error("REMINDER TIME ALREADY PASSED");
        return;
      }
    }

    try {
      setSubmitting(true);
      await createSchedule({
        message: message.trim(),
        scheduled_at: iso,
        sound_enabled: alarmTone !== "off",
        display_mode: displayMode,
        reminder_minutes: rm,
        repeat,
        text_size: textSize,
        bg_colors: bgEnabled ? bgColors : null,
        alarm_tone: alarmTone,
      });
      toast.success("FLASH SCHEDULED");
      setMessage("");
      setWhen(defaultDateTime());
      setAlarmTone("beep");
      setDisplayMode("fit");
      setReminderEnabled(false);
      setReminderMinutes(10);
      setCustomReminder("");
      setRepeat("none");
      setTextSize("md");
      setBgEnabled(false);
      setBgColors(["#FF3311"]);
      onCreated?.();
    } catch (err) {
      toast.error("FAILED TO SCHEDULE");
    } finally {
      setSubmitting(false);
    }
  };

  const effectiveReminder = customReminder
    ? parseInt(customReminder, 10)
    : reminderMinutes;

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="scheduler-form"
      className="bg-white brutal-border brutal-shadow p-6 md:p-8"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-[#FF3311] brutal-border w-10 h-10 flex items-center justify-center">
          <Zap className="w-6 h-6 text-white" strokeWidth={3} />
        </div>
        <h2 className="font-display text-2xl md:text-3xl uppercase">
          {t("form.title")}
        </h2>
      </div>

      <div className="space-y-5">
        <div>
          <label
            htmlFor="flash-message"
            className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold"
          >
            Message to flash
          </label>
          <textarea
            id="flash-message"
            data-testid="input-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Take out the trash"
            rows={2}
            maxLength={200}
            className="w-full bg-white brutal-border p-4 text-lg font-display focus:outline-none focus:bg-[#FFF9B0] transition-colors resize-none"
          />
          <div className="text-right font-mono text-xs text-neutral-500 mt-1">
            {message.length}/200
          </div>
        </div>

        <div>
          <label
            htmlFor="flash-when"
            className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold"
          >
            Date &amp; time
          </label>
          <input
            id="flash-when"
            data-testid="input-datetime"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="w-full bg-white brutal-border p-4 text-lg font-mono focus:outline-none focus:bg-[#FFF9B0] transition-colors"
          />
        </div>

        {/* Reminder */}
        <div>
          <button
            type="button"
            data-testid="toggle-reminder"
            onClick={() => setReminderEnabled((v) => !v)}
            className={`w-full flex items-center justify-between gap-3 brutal-border p-4 brutal-shadow-sm brutal-interactive ${
              reminderEnabled ? "bg-[#00CCFF]" : "bg-white"
            }`}
          >
            <div className="flex items-center gap-3">
              {reminderEnabled ? (
                <Bell className="w-5 h-5" strokeWidth={3} />
              ) : (
                <BellOff className="w-5 h-5" strokeWidth={3} />
              )}
              <span className="font-display uppercase text-base">
                Pre-flash reminder {reminderEnabled ? "ON" : "OFF"}
              </span>
            </div>
            <span className="font-mono text-xs uppercase tracking-widest">
              {reminderEnabled
                ? `[${formatLeadTime(effectiveReminder) || "—"} BEFORE]`
                : "[disabled]"}
            </span>
          </button>

          {reminderEnabled && (
            <div
              data-testid="reminder-controls"
              className="mt-3 brutal-border p-4 bg-[#F2F2F0]"
            >
              <div className="font-mono text-xs uppercase tracking-widest mb-2 font-bold">
                Flash this far before
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {PRESETS.map((p) => {
                  const active = !customReminder && reminderMinutes === p.value;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      data-testid={`preset-${p.value}`}
                      onClick={() => {
                        setReminderMinutes(p.value);
                        setCustomReminder("");
                      }}
                      className={`brutal-border px-3 py-2 font-mono text-sm uppercase brutal-shadow-sm brutal-interactive ${
                        active ? "bg-[#E5FF00]" : "bg-white"
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <label className="block font-mono text-[10px] uppercase tracking-widest mb-1 font-bold text-neutral-600">
                Or custom (minutes)
              </label>
              <input
                data-testid="input-custom-reminder"
                type="number"
                min="1"
                max="525600"
                value={customReminder}
                onChange={(e) => setCustomReminder(e.target.value)}
                placeholder="e.g. 45"
                className="w-full bg-white brutal-border p-3 font-mono focus:outline-none focus:bg-[#FFF9B0] transition-colors"
              />
            </div>
          )}
        </div>

        <div>
          <label className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold">
            Repeat
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "none", l: "Once" },
              { v: "daily", l: "Daily" },
              { v: "weekly", l: "Weekly" },
            ].map((r) => (
              <button
                key={r.v}
                type="button"
                data-testid={`repeat-${r.v}`}
                onClick={() => setRepeat(r.v)}
                className={`flex items-center justify-center gap-1 brutal-border p-3 font-display uppercase text-sm brutal-shadow-sm brutal-interactive ${
                  repeat === r.v ? "bg-[#E5FF00]" : "bg-white"
                }`}
              >
                {r.v !== "none" && <Repeat className="w-4 h-4" strokeWidth={3} />}
                {r.l}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold">
            Display mode
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="mode-fit"
              onClick={() => setDisplayMode("fit")}
              className={`flex items-center gap-2 brutal-border p-3 brutal-shadow-sm brutal-interactive ${
                displayMode === "fit" ? "bg-[#E5FF00]" : "bg-white"
              }`}
            >
              <Maximize2 className="w-5 h-5" strokeWidth={3} />
              <div className="text-left">
                <div className="font-display uppercase text-sm leading-tight">Fit screen</div>
                <div className="font-mono text-[10px] uppercase text-neutral-600">Always visible</div>
              </div>
            </button>
            <button
              type="button"
              data-testid="mode-scroll"
              onClick={() => setDisplayMode("scroll")}
              className={`flex items-center gap-2 brutal-border p-3 brutal-shadow-sm brutal-interactive ${
                displayMode === "scroll" ? "bg-[#E5FF00]" : "bg-white"
              }`}
            >
              <MoveHorizontal className="w-5 h-5" strokeWidth={3} />
              <div className="text-left">
                <div className="font-display uppercase text-sm leading-tight">Scroll</div>
                <div className="font-mono text-[10px] uppercase text-neutral-600">Marquee across</div>
              </div>
            </button>
          </div>
        </div>

        <div>
          <label className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold">
            <Type className="inline w-3 h-3 mr-1" strokeWidth={3} />
            Text size
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "sm", l: "Small" },
              { v: "md", l: "Medium" },
              { v: "lg", l: "Large" },
            ].map((s) => (
              <button
                key={s.v}
                type="button"
                data-testid={`size-${s.v}`}
                onClick={() => setTextSize(s.v)}
                className={`brutal-border p-3 font-display uppercase brutal-shadow-sm brutal-interactive ${
                  textSize === s.v ? "bg-[#E5FF00]" : "bg-white"
                } ${s.v === "sm" ? "text-xs" : s.v === "md" ? "text-sm" : "text-base"}`}
              >
                {s.l}
              </button>
            ))}
          </div>
        </div>

        <div>
          <button
            type="button"
            data-testid="toggle-bg"
            onClick={() => setBgEnabled((v) => !v)}
            className={`w-full flex items-center justify-between gap-3 brutal-border p-4 brutal-shadow-sm brutal-interactive ${
              bgEnabled ? "bg-[#FFDD00]" : "bg-white"
            }`}
          >
            <div className="flex items-center gap-3">
              <Palette className="w-5 h-5" strokeWidth={3} />
              <span className="font-display uppercase text-base">
                Custom colors {bgEnabled ? "ON" : "OFF"}
              </span>
            </div>
            <span className="font-mono text-xs uppercase tracking-widest">
              {bgEnabled ? `${bgColors.length} color${bgColors.length > 1 ? "s" : ""}` : "[rainbow]"}
            </span>
          </button>

          {bgEnabled && (
            <div data-testid="bg-controls" className="mt-3 brutal-border p-4 bg-[#F2F2F0] space-y-3">
              <div className="font-mono text-xs uppercase tracking-widest font-bold">
                Pick 1-3 colors
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {bgColors.map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-1 brutal-border bg-white p-1"
                  >
                    <input
                      data-testid={`bg-color-${i}`}
                      type="color"
                      value={c}
                      onChange={(e) => {
                        const next = [...bgColors];
                        next[i] = e.target.value;
                        setBgColors(next);
                      }}
                      className="w-10 h-10 cursor-pointer border-0 p-0"
                    />
                    <button
                      type="button"
                      data-testid={`bg-remove-${i}`}
                      onClick={() => {
                        if (bgColors.length === 1) return;
                        setBgColors(bgColors.filter((_, j) => j !== i));
                      }}
                      disabled={bgColors.length === 1}
                      className="font-mono text-xs px-2 py-1 disabled:opacity-30"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {bgColors.length < 3 && (
                  <button
                    type="button"
                    data-testid="bg-add"
                    onClick={() =>
                      setBgColors([
                        ...bgColors,
                        ["#E5FF00", "#00CCFF", "#FF3311"][bgColors.length] || "#FFFFFF",
                      ])
                    }
                    className="brutal-border px-3 py-2 font-mono text-xs uppercase brutal-shadow-sm brutal-interactive bg-white"
                  >
                    + Add
                  </button>
                )}
              </div>
              <div className="font-mono text-[10px] uppercase text-neutral-600">
                1 color = solid background · 2-3 colors cycle
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          data-testid="toggle-sound"
          onClick={() => {}}
          style={{ display: "none" }}
          aria-hidden="true"
        />

        <div>
          <label className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold">
            <Music2 className="inline w-3 h-3 mr-1" strokeWidth={3} /> Alarm tone
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {TONE_LABELS.map((t) => {
              const active = alarmTone === t.v;
              const isPlaying = previewing === t.v;
              return (
                <div key={t.v} className="flex items-stretch gap-1">
                  <button
                    type="button"
                    data-testid={`tone-${t.v}`}
                    onClick={() => setAlarmTone(t.v)}
                    className={`flex-1 brutal-border p-2 font-display uppercase text-xs brutal-shadow-sm brutal-interactive ${
                      active ? "bg-[#E5FF00]" : "bg-white"
                    }`}
                  >
                    {t.l}
                  </button>
                  {t.v !== "off" && (
                    <button
                      type="button"
                      data-testid={`preview-${t.v}`}
                      onClick={(e) => {
                        e.preventDefault();
                        primeAudio();
                        if (isPlaying) {
                          stopAllTones();
                          setPreviewing(null);
                        } else {
                          stopAllTones();
                          startTone(t.v);
                          setPreviewing(t.v);
                          window.setTimeout(() => {
                            stopAllTones();
                            setPreviewing((cur) => (cur === t.v ? null : cur));
                          }, 2500);
                        }
                      }}
                      className={`brutal-border px-2 flex items-center justify-center brutal-shadow-sm brutal-interactive ${
                        isPlaying ? "bg-[#FF3311] text-white" : "bg-white"
                      }`}
                      title="Preview 2.5s"
                    >
                      <Play className="w-3 h-3" strokeWidth={3} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="submit"
          data-testid="submit-schedule"
          disabled={submitting}
          className="w-full bg-[#FF3311] text-white brutal-border brutal-shadow brutal-interactive font-display uppercase text-xl md:text-2xl py-5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Scheduling..." : "Schedule Flash →"}
        </button>

        <button
          type="button"
          data-testid="save-as-template-btn"
          onClick={openSaveTemplate}
          className="w-full bg-white brutal-border brutal-shadow-sm brutal-interactive font-display uppercase text-sm md:text-base py-3 flex items-center justify-center gap-2"
        >
          <Bookmark className="w-4 h-4" strokeWidth={3} />
          {t("tpl.saveBtn")}
        </button>
      </div>

      {showSaveTpl && (
        <SaveTemplateDialog
          draft={templateDraft()}
          onClose={() => setShowSaveTpl(false)}
          onSaved={() => onTemplateSaved?.()}
        />
      )}
    </form>
  );
};

export default SchedulerForm;
